// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { ElementType, ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { BASE_DOCUMENT_TITLE, documentTitleFor } from "@/lib/document-title";

interface Breadcrumb {
  label: string;
  to: string;
}

interface PageTitleState {
  title: string;
  description?: ReactNode;
  breadcrumbs?: Breadcrumb[];
  icon?: ElementType;
}

const PageTitleContext = createContext<{
  state: PageTitleState | null;
  set: (s: PageTitleState | null) => void;
}>({ state: null, set: () => {} });

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [state, set] = useState<PageTitleState | null>(null);

  // Same trail the header renders, so the tab reads `… · Chains · ASDA`.
  const documentTitle = documentTitleFor([...(state?.breadcrumbs?.map((crumb) => crumb.label) ?? []), state?.title]);

  useEffect(
    function synchronizeDocumentTitle() {
      document.title = documentTitle;
      // Leaving the authenticated shell (logout) drops back to the bare title.
      return () => {
        document.title = BASE_DOCUMENT_TITLE;
      };
    },
    [documentTitle],
  );

  return <PageTitleContext.Provider value={{ state, set }}>{children}</PageTitleContext.Provider>;
}

export function usePageTitle() {
  return useContext(PageTitleContext).state;
}

export function useSetPageTitle(
  title: string,
  description?: ReactNode,
  breadcrumbs?: Breadcrumb[],
  icon?: ElementType,
) {
  const { set } = useContext(PageTitleContext);
  useEffect(
    function updatePageTitleInContext() {
      set({ title, description, breadcrumbs, icon });
      return () => set(null);
    },
    [title, description, breadcrumbs, icon, set],
  );
}
